import React from 'react';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ArrowRightIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const cn = (...classes) => classes.filter(Boolean).join(' ');

const VARIANTS = {
  info: {
    Icon: InformationCircleIcon,
    cardBg:
      'bg-blue-50/70 ring-1 ring-blue-200 dark:bg-sky-500/10 dark:ring-sky-500/30',
    iconWrap:
      'bg-blue-100 text-blue-600 dark:bg-sky-500/20 dark:text-sky-300',
    text: 'text-blue-900 dark:text-sky-100',
    button:
      'bg-blue-600 text-white hover:bg-blue-700 dark:bg-sky-500 dark:hover:bg-sky-400',
  },
  warning: {
    Icon: ExclamationTriangleIcon,
    cardBg:
      'bg-amber-50/70 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:ring-amber-500/30',
    iconWrap:
      'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300',
    text: 'text-amber-900 dark:text-amber-100',
    button:
      'bg-amber-600 text-white hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-400',
  },
  error: {
    Icon: XCircleIcon,
    cardBg:
      'bg-rose-50/70 ring-1 ring-rose-200 dark:bg-rose-500/10 dark:ring-rose-500/30',
    iconWrap:
      'bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-300',
    text: 'text-rose-900 dark:text-rose-100',
    button:
      'bg-rose-600 text-white hover:bg-rose-700 dark:bg-rose-500 dark:hover:bg-rose-400',
  },
  success: {
    Icon: CheckCircleIcon,
    cardBg:
      'bg-emerald-50/70 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:ring-emerald-500/30',
    iconWrap:
      'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300',
    text: 'text-emerald-900 dark:text-emerald-100',
    button:
      'bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-400',
  },
};

const Alert = ({ title, description, type = 'info', buttonText, navigate }) => {
  const v = VARIANTS[type] || VARIANTS.info;
  const { Icon } = v;

  return (
    <div
      className={cn(
        'group relative flex items-start gap-3 rounded-lg px-4 py-3.5 sm:items-center sm:gap-4 sm:px-5',
        v.cardBg,
      )}
    >
      <span
        className={cn(
          'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full',
          v.iconWrap,
        )}
      >
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>

      <div className="min-w-0 flex-1">
        {title && (
          <p className={cn('text-sm font-bold', v.text)}>{title}</p>
        )}
        <p className={cn('text-sm leading-relaxed', v.text)}>{description}</p>
      </div>

      {buttonText && navigate && (
        <Link
          to={navigate}
          className={cn(
            'inline-flex flex-shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold uppercase tracking-wide shadow-sm transition active:scale-[0.98]',
            v.button,
          )}
        >
          {buttonText}
          <ArrowRightIcon
            className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </Link>
      )}
    </div>
  );
};

export default Alert;
